import API, { ensurePrefixed } from 'utils/api';
import { onError, onSuccess } from 'utils/handlers';
import {
  // SET_TIPOS_DE_APLICACION_CARGOS,
  SET_TIPOS_DE_APLICACION_DESCUENTOS,
  SET_TIPOS_DE_APLICACION,
  SET_SERVICIOS_DEL_VEHICULO,
  SET_ESTADOS_DEL_VEHICULO,
  SET_TIPOS_DE_VEHICULOS,
  SET_CLASES_DE_VEHICULOS,
  SET_TIPOS_DE_APLICACION_CARGOS,
  SET_ESTATUS_DE_DEPOSITOS_EN_GARANTIA,
  SET_TIPOS_DE_MOTOCICLETAS,
  SET_PERIODOS,
} from 'store/reducers/catalogs';
import { SET_PERIODO_FISCAL_SELECTED } from 'store/reducers/auth';
import getFormattedValues, { formatReceived, removeAccents } from 'utils/formatValues';
import { store } from 'store';
import { SET_CARGOS_PUBLICO_GENERAL } from 'store/reducers/caja';
import { getConsultaTask } from 'api/utils/consultaTask';
import { getDistinctValues } from 'utils/normalizers';
import { getPlantillaIndexada } from 'api/tramites/plantillasDeTramites';
import { message } from 'antd';

const { periodoFiscalSelected } = store.getState().auth;

/**
 * GET Periodos
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getPeriodos = async (params = null) => {
  try {
    const { periodos } = store.getState().catalogs;
    if (!periodos?.length) {
      const response = await API.get('recaudacion/periodos', { params });
      store.dispatch({
        type: SET_PERIODOS,
        payload: response.data,
      });
      return response.data;
    }
    return periodos;
  } catch (err) {
    onError(err);
  }
  return [];
};

/**
 * GET Tipos de Aplicaciones para Cargos
 * @returns {Promise<object[]>}
 */
export const getTiposDeAplicacion = async (filter = false) => {
  try {
    const filterFunction = filter ? (e) => ![2, 4].includes(e.id) : () => true;
    const { tiposDeAplicacion } = store.getState().catalogs;
    if (!tiposDeAplicacion?.length) {
      const response = await API.get('/recaudacion/tipos-de-aplicacion/');
      store.dispatch({
        type: SET_TIPOS_DE_APLICACION,
        payload: response.data,
      });
      return response.data.filter(filterFunction);
    }
    return tiposDeAplicacion.filter(filterFunction);
  } catch (err) {
    onError(err);
    return [];
  }
};
/**
 * GET Alertas Vehiculares
 * @param {number} id ID del vehiculo
 * @returns {Promise<object[]>}
 */
export const getAlertasVehiculares = async (id) => {
  try {
    const response = await API.get(`/recaudacion/vehiculos/${id}/alertas/`);
    return formatReceived(response.data);
  } catch (err) {
    onError(err);
  }
  return [];
};
/**
 * POST Generar Cargos Determinables
 * @param {object} values
 * @returns {Promise<Array|boolean>}
 */
export const postGenerarCargosDeterminables = async (values) => {
  try {
    const response = await API.post('/recaudacion/cargos/generar-determinables/', values);
    onSuccess(response, 'Proceso de generación de determinables iniciado con éxito.');
    store.dispatch({
      type: SET_PERIODO_FISCAL_SELECTED,
      payload: { ...periodoFiscalSelected, determinables_aplicados: true },
    });
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * POST Generar Cargos Determinables por Cargo
 * @param {number} tipo_de_cargo
 * @returns {Promise<boolean>}
 */
export const postGenerarCargosDeterminablesPorCargo = async (tipo_de_cargo) => {
  try {
    const response = await API.post('/recaudacion/cargos/generar-determinables/', {
      periodo_fiscal: periodoFiscalSelected?.id,
      tipo_de_cargo,
    });
    onSuccess(response, 'Proceso de generación de determinables iniciado con éxito.');
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};

const parser = (_data) => {
  const data = { ..._data };
  if ('importe' in data) {
    data.importe = parseFloat(data.importe);
  }
  if ('importe_total' in data) {
    data.importe_total = parseFloat(data.importe_total);
  }
  return data;
};

const parseCargos = (cargos) => cargos.map((cargo) => ({
  ...parser(cargo),
  importe: parseFloat(cargo.importe),
  descuentos_aplicables: cargo.descuentos_aplicables.map(parser),
  descuentos_especiales: cargo.descuentos_especiales.map(parser),
  recargos: cargo.recargos.map((recargo) => ({
    ...parser(recargo),
    descuentos: recargo.descuentos.map(parser),
  })),
  actualizaciones: cargo.actualizaciones.map((actualizacion) => ({
    ...parser(actualizacion),
    descuentos: actualizacion.descuentos.map(parser),
  })),
  gastos: cargo.gastos.map((gasto) => ({
    ...parser(gasto),
    descuentos: gasto.descuentos.map(parser),
  })),
}));

const getPlantillasDeCargos = async (_cargos = []) => {
  if (_cargos.some((c) => c.tramite)) {
    const cargos = _cargos.filter((c) => c.tramite);
    const plantillasIds = getDistinctValues(cargos
      .map((c) => c.tramite.plantilla).filter((p) => p));
    const plantillas = await Promise.all(plantillasIds.map((p) => getPlantillaIndexada(p)));
    return plantillas.filter((p) => p).map((plantilla) => ({
      ...plantilla,
      disabledOrdenPago: plantilla.pasos_para_tramites.some((p) => p.otro_formato),
      use_json: plantilla.pasos_para_tramites.some((p) => p.no_afectar_padron),
    }));
  }
  return [];
};

/**
 * POST Obtener Cargos de un Padron
 * @param {number} padron Tipo de Padron
 * @param {number} padron_id ID del padron
 * @param {boolean} timeout intento anterior del request con timeout
 * @returns {Promise<object>}
 */
export const postConsultarCargosPorPadron = async (
  padron,
  padron_id,
  timeout = false,
  getPlantillas = true,
  sistema = null,
) => {
  try {
    const values = { padron, padron_id, sistema };
    if (timeout) {
      values.timeout = timeout;
    }
    values.idPadron = padron;
    const response = await API.post('/recaudacion/consulta-caja/padron/', values, { accionInf: 'CONSULTA_CAJA' });
    const data = (Array.isArray(response.data) ? response.data[0] : response.data);
    if (getPlantillas) {
      data.plantillas = await getPlantillasDeCargos(data.cargos);
    }
    const { tramites_bloquear_adeudos } = data;
    delete data.tramites_bloquear_adeudos;
    if (Array.isArray(tramites_bloquear_adeudos)) {
      data.tramites_bloqueando_adeudos = tramites_bloquear_adeudos.map((t) => t.id);
    } else if (tramites_bloquear_adeudos) {
      data.tramites_bloqueando_adeudos = [tramites_bloquear_adeudos.id];
    } else {
      data.tramites_bloqueando_adeudos = [];
    }
    return formatReceived({
      ...data,
      alerta: formatReceived(data?.alerta || []),
      cargos: formatReceived(parseCargos(data?.cargos || [])),
      convenios: formatReceived(data?.convenios || []),
    });
  } catch (err) {
    const isTimeoutError = /timeout/i.test(err.message);
    if (isTimeoutError && !timeout) {
      return postConsultarCargosPorPadron(padron, padron_id, true, getPlantillas);
    }
    onError(err);
    return {};
  }
};

/**
 * POST Consultar Cargos Público General
 * @returns {Promise<object[]>}
 */
export const postConsultarCargosPublicoGeneral = async (cargos = []) => {
  try {
    const response = await API.post('/recaudacion/consulta-cargos/publico-general/', { cargos }, { accionInf: 'INICIAR_CAJA' });
    return formatReceived(response.data.cargos);
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * POST Para Generar Gastos
 * @param {Object} values
 * @returns {Promise<Boolean>}
 */
export const postGenerarGastos = async (values) => {
  try {
    const response = await API.post('/recaudacion/gastos/', values);
    onSuccess(response, 'Agregado correctamente');
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Tipos de Aplicaciones para Descuentos
 * @returns {Promise<object[]>}
 */
export const getTiposDeAplicacionDeDescuentos = async () => {
  try {
    const { tiposDeAplicacionParaDescuentos } = store.getState().catalogs;
    if (!tiposDeAplicacionParaDescuentos?.length) {
      const response = await API.get('/recaudacion/aplicacion-de-los-descuentos/');
      store.dispatch({
        type: SET_TIPOS_DE_APLICACION_DESCUENTOS,
        payload: response.data,
      });
      return response.data;
    }
    return tiposDeAplicacionParaDescuentos;
  } catch (err) {
    onError(err);
    return [];
  }
};
/**
 * GET Tipos de Aplicaciones para Cargos
 * @returns {Promise<object[]>}
 */
export const getTiposDeAplicacionParaCargos = async () => {
  try {
    const { tiposDeAplicacionCargos } = store.getState().catalogs;
    if (!tiposDeAplicacionCargos?.length) {
      const response = await API.get('/recaudacion/tipos-de-aplicaciones-para-cargos/');
      store.dispatch({
        type: SET_TIPOS_DE_APLICACION_CARGOS,
        payload: response.data,
      });
      return response.data;
    }
    return tiposDeAplicacionCargos;
  } catch (err) {
    onError(err);
    return [];
  }
};

const postAplicarDescuentoEspecial = async (values, cargo,
  { clave, idPadron, cargoCompleto } = {}) => {
  try {
    const accionInf = ensurePrefixed('APLICAR_', 'DESCUENTO');
    const infoCargoAplicado = cargoCompleto
      ? { descripcion: cargoCompleto.descripcion, periodo: cargoCompleto.periodo }
      : undefined;
    const response = await API.post('/recaudacion/descuentos/', {
      ...values, cargo, infoCargoAplicado,
    }, { accionInf, idPadron, clave });
    return response.data;
  } catch (_error) {
    if (_error?.response?.headers['content-type'].includes('application/json')) {
      let error = _error.response.data.detail || _error.response.data;
      if (_error.response.data.detail) {
        return { ...values, error, cargo };
      }
      const keys = Object.keys(error);
      error = keys.length ? error[keys[0]] : 'Error desconocido';
      return { ...values, error, cargo };
    }
    return { ...values, error: _error.message, cargo };
  }
};

/**
 * @desc POST Para Generar descuentos especiales
 * @param {Object} values
 * @param {Array} cargos
 * @returns {Promise<Array|boolean>}
 */
export const postGenerarDescuentosEspeciales = async (values, cargos = [],
  { clave, idPadron, cargosCompletos = [] } = {}) => {
  try {
    const promises = cargos.map((cargo) => {
      const cargoCompleto = cargosCompletos.find((c) => c.id === cargo);
      return postAplicarDescuentoEspecial(values, cargo, { clave, idPadron, cargoCompleto });
    });
    const responses = await Promise.allSettled(promises);

    const success = [];
    const failed = [];

    responses.forEach((response) => {
      if (response.status === 'fulfilled' && response.value && !response.value.error) {
        success.push(response.value);
      } else {
        failed.push({ ...response.value });
      }
    });

    if (success.length) {
      onSuccess(null, 'Descuento aplicado correctamente');
    }

    const entries = failed.map((e) => {
      const value = failed
        .filter((i) => i.tipo_de_descuento === e.tipo_de_descuento)
        .map((i) => i.cargo);
      return [e.error, value];
    });

    const objectErrors = Object.fromEntries(entries);

    return {
      success: !!success.length,
      objectErrors,
      errorKeys: Object.keys(objectErrors),
    };
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * PUT Descuentos
 * @param {Object} values
 * @param {number} values.importe
 * @param {number} id
 * @returns {Promise<Array|boolean>}
 */
export const putDescuentos = async (values, id, { clave, idPadron } = {}) => {
  try {
    const accionInf = ensurePrefixed('EDITAR_', 'DESCUENTO');
    const response = await API.patch(`/recaudacion/descuentos/${id}`, values, {
      accionInf, idPadron, clave,
    });
    onSuccess(null, 'Descuento Editado Correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Obtener reporte
 * @param {number} key
 * @param {string} reporte
 * @param {string} cierre
 * @returns {Promise <object|boolean>}
 */
export const getReporte = async (key, reporte, cierre = '') => {
  const uri = `/recaudacion/reportes/${key}${reporte}${cierre}`;
  const defMessage = 'El reporte se está generando espere un momento...';
  const [data] = await getConsultaTask(
    null,
    uri,
    defMessage,
    null,
    [],
  );
  if (data) {
    const base64 = `data:application/pdf;base64,${data.pdf}`;
    return { success: true, base64 };
  }
  return [];
};

/**
 * GET Servicios del Vehiculo
 * @returns {Promise<object[]>}
 */
export const getServiciosDeVehiculo = async () => {
  try {
    const { serviciosDelVehiculo } = store.getState().catalogs;
    if (!serviciosDelVehiculo?.length) {
      const response = await API.get('/recaudacion/servicios-del-vehiculo/');
      store.dispatch({
        type: SET_SERVICIOS_DEL_VEHICULO,
        payload: response.data,
      });
      return response.data;
    }
    return serviciosDelVehiculo;
  } catch (err) {
    onError(err);
    return [];
  }
};
/**
 * GET Servicios del Vehiculo
 * @param {number} params
 * @returns {Promise<object[]>}
 */
export const getServiciosDelVehiculo = async (params) => {
  try {
    const { serviciosDelVehiculo } = store.getState().catalogs;
    if (!serviciosDelVehiculo.length) {
      const response = await API.get('recaudacion/servicios-del-vehiculo/', { params });
      store.dispatch({
        type: SET_SERVICIOS_DEL_VEHICULO,
        payload: response.data,
      });
      return response.data;
    }
    return serviciosDelVehiculo;
  } catch (err) {
    onError(err);
  }
  return [];
};

/**
 * GET Estados del Vehiculo
 * @returns {Promise<object[]>}
 */
export const getEstadosDeVehiculo = async () => {
  try {
    const { estadosDelVehiculo } = store.getState().catalogs;
    if (!estadosDelVehiculo?.length) {
      const response = await API.get('/recaudacion/estados-del-vehiculo/');
      store.dispatch({
        type: SET_ESTADOS_DEL_VEHICULO,
        payload: response.data,
      });
      return response.data;
    }
    return estadosDelVehiculo;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Marcas de Vehiculos
 * @returns {Promise<object[]>}
 */
export const getMarcasDeVehiculos = async (params = null) => {
  try {
    const response = await API.get('/recaudacion/marcas-de-vehiculos/', { params });
    if (response.data.results?.length) {
      return response.data;
    }
  } catch (err) {
    onError(err);
  }
  return { results: [] };
};

/**
 * GET Marcas de Vehiculos
 * @returns {Promise<object[]>}
 */
export const getMarcasDeVehiculosSinPaginar = async (params = null) => {
  try {
    const response = await API.get('/recaudacion/marcas-de-vehiculos-public/', { params });
    return response.data;
  } catch (err) {
    onError(err);
  }
  return [];
};

/**
 * GET Lineas de Vehiculos
 * @returns {Promise<object[]>}
 */
export const getLineasDeVehiculos = async (params) => {
  try {
    const response = await API.get('/recaudacion/lineas-vehiculares/', { params });
    return response.data;
  } catch (err) {
    onError(err);
    return { results: [] };
  }
};

/**
 * GET Para obtener los cargos de un vehiculo
 * @param {Object} params
 * @returns {Promise<object[]>}
 */
export const getVehiculoCaja = async (params = null) => {
  try {
    const response = await API.get('recaudacion/vehiculos-caja', { params });
    if (params?.id && response.data.length === 1) {
      const { results: [linea] } = await getLineasDeVehiculos({ id: response.data[0].linea });
      response.data[0].marca = linea?.marca;
    }
    return formatReceived(response.data, true);
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Lineas Vehiculares
 * @param {number} params
 * @returns {Promise<object[]>}
 */
export const getLineasVehiculares = async (params) => {
  try {
    const response = await API.get('recaudacion/lineas-vehiculares/', { params });
    return response.data;
  } catch (err) {
    onError(err);
  }
  return [];
};

/**
 * GET Tipos de Vehiculos
 * @returns {Promise<object[]>}
 */
export const getTiposDeVehiculos = async () => {
  try {
    const { tiposDeVehiculos } = store.getState().catalogs;
    if (tiposDeVehiculos?.length) return tiposDeVehiculos;
    const response = await API.get('/recaudacion/tipos-de-vehiculos/');
    store.dispatch({
      type: SET_TIPOS_DE_VEHICULOS,
      payload: response.data,
    });
    return response.data;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Tipos de Vehiculos
 * @returns {Promise<object[]>}
 */
export const getTiposDeMotocicletas = async () => {
  try {
    const { tiposDeMotocicletas } = store.getState().catalogs;
    if (!tiposDeMotocicletas?.length) {
      const response = await API.get('/recaudacion/tipos-de-motocicletas/');
      store.dispatch({
        type: SET_TIPOS_DE_MOTOCICLETAS,
        payload: response.data,
      });
      return response.data;
    }
    return tiposDeMotocicletas;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET ColoresVehiculos
 * @returns {Promise<object[]>}
 */
export const getColoresVehiculos = async (params = null) => {
  try {
    const response = await API.get('/catalogos/catalogos-de-colores-vehiculares/', { params });
    return response.data;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET ColoresVehiculos
 * @returns {Promise<object||null>}
 */
export const getColorVehiculo = async (id) => {
  try {
    const response = await API.get(`/catalogos/catalogos-de-colores-vehiculares/${id}`);
    return response.data;
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * GET Clases de Vehiculos
 * @returns {Promise<object[]>}
 */
export const getClasesDeVehiculos = async () => {
  try {
    const { clasesDeVehiculos } = store.getState().catalogs;
    if (!clasesDeVehiculos?.length) {
      const response = await API.get('/recaudacion/clases-de-vehiculos/');
      store.dispatch({
        type: SET_CLASES_DE_VEHICULOS,
        payload: response.data,
      });
      return response.data;
    }
    return clasesDeVehiculos;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Obtener todos los cargos del ciudadano inclyendo todos los padrones que le pertenezcan
 * @param {number} key
 * @returns {Promise<object[]>}
 */
export const getConsultarCargosCiudadanos = async (key) => {
  try {
    const response = await API.get(`/recaudacion/consulta-caja/${key}/`);
    const { padrones } = store.getState().catalogs;
    const mapTipoPadronStr = {
      casasdeempenio: 'Casa de Empeño',
      juegodeazar: 'Juegos de Azar',
      lotedepanteon: 'Lotes De Panteon',
    };
    return formatReceived(response.data.map((padron) => {
      const tipoDePadronDesc = mapTipoPadronStr[padron.tipo_de_padron] || padron.tipo_de_padron;
      const tipo_de_padron = padrones
        .find((p) => removeAccents(p.descripcion.toLowerCase())
          === tipoDePadronDesc.toLowerCase())?.id;
      return {
        ...padron,
        cargos: formatReceived(padron.cargos),
        tipo_de_padron,
      };
    }));
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * POST Generar Cargos
 * @param {Object} values
 * @param {Number} times
 * @returns {Promise<boolean>}
 */
export const postGenerarCargos = async (values, times = 1, { clave } = {}) => {
  try {
    const accionInf = ensurePrefixed('GENERAR_', 'CARGO');
    const idPadron = values.padron;
    const promises = [];
    for (let i = 0; i < times; i += 1) {
      promises.push(API.post('recaudacion/generar-cargos/', {
        ...getFormattedValues(values),
        tramite: values.tramite || null,
      }, { accionInf, idPadron, clave }));
    }
    const responses = await Promise.all(promises);
    const datos = responses.map((e) => formatReceived(e.data));
    const { entidad: { ciudadano_publico_en_general } } = store.getState().auth;
    if (ciudadano_publico_en_general && values.padron === 1
      && values.padron_id === ciudadano_publico_en_general) {
      const { cargosPublicoGeneral } = store.getState().caja;
      store.dispatch({
        type: SET_CARGOS_PUBLICO_GENERAL,
        payload: [
          ...cargosPublicoGeneral,
          ...datos.reduce((acum, curr) => acum.concat(curr.map((c) => c.id)), []),
        ],
      });
    }
    return datos;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 *
 * @param {Number} key
 * @returns {Promise<Object>}
 */
export const getConstancia = async (key) => {
  try {
    const response = await API
      .get(`/recaudacion/reportes/${key}/constancia-de-situacion-municipal/`);
    const base64 = `data:application/pdf;base64,${response.data.pdf}`;
    return { success: true, base64 };
  } catch (err) {
    onError(err);
    return { success: false };
  }
};

/**
 * Get Órdenes De Pago
 * @param {object} params
 * @param {string} params.folio
 * @returns {Promise<Object>}
 */
export const getOrdenDePago = async (params) => {
  try {
    const response = await API.get('recaudacion/referencias-de-pago', { params });
    return response.data[0] || response.data.results[0];
  } catch (err) {
    onError(err);
    return null;
  }
};

export const getMovimientosDeDeposito = async (params = null) => {
  try {
    const response = await API.get('/recaudacion/movimientos-de-deposito-de-garantias/', { params });
    return formatReceived(response.data);
  } catch (err) {
    onError(err);
    return [];
  }
};
/**
 * POST Para Generar Determinables por Tipo de Cargo
 * @param {Object} values
 * @returns {Promise<Boolean>}
 */
export const postGenerarDeterminablesPorTipoDeCargo = async (values) => {
  try {
    const response = await API.post('/recaudacion/cargos/generar-determinables-por-tipo-de-cargo/', values);
    onSuccess(response, 'Proceso de generación de determinables iniciado con éxito. Recibirá un correo electrónico cuando finalice.');
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * POST Para Generar Determinables por Tipo de Cargo
 * @param {Object} values
 * @returns {Promise<Boolean>}
 */
export const postGenerarDeterminablesPorPadrones = async (values) => {
  try {
    const response = await API.post('/recaudacion/cargos/generar-determinable-por-padron/', values);
    onSuccess(response, 'Proceso de generación de determinables iniciado con éxito. Recibirá un correo electrónico cuando finalice.');
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * POST Para Generar Determinables por Tipo de Padron
 * @param {Object} values
 * @returns {Promise<Boolean>}
 */
export const postGenerarDeterminablesPorPadron = async (values) => {
  try {
    const response = await API.post('/recaudacion/cargos/generar-determinable-por-tipo-de-padron/', values);
    onSuccess(response, 'Proceso de generación de determinables iniciado con éxito. Recibirá un correo electrónico cuando finalice.');
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Estatus de Depositos en Garantia
 * @returns {Promise<Object>}
 */
export const getEstatusDeDepositoEnGarantia = async () => {
  try {
    const { estadusDeDepositosEnGarantia } = store.getState().catalogs;
    if (!estadusDeDepositosEnGarantia?.length) {
      const response = await API.get('/recaudacion/estatus-de-los-depositos/');
      store.dispatch({
        type: SET_ESTATUS_DE_DEPOSITOS_EN_GARANTIA,
        payload: response.data,
      });
      return response.data;
    }
    return estadusDeDepositosEnGarantia;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Placas
 * @returns {Promise<Object>}
 * @param {Object} params
*/
export const getPlacas = async (params) => {
  try {
    const response = await API.get('/recaudacion/placas/', { params });
    return formatReceived(response.data);
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 *
 * @param {object} values
 * @returns {promise<Object>}
 */
export const postConsultaFolio = async (values) => {
  try {
    const response = await API.post('/recaudacion/consulta-cargos/folio/', values);
    if (![200, 206].includes(response.status)) {
      message.info('No se encontraron coincidencias');
      return false;
    }
    response.data.plantillas = await getPlantillasDeCargos(response.data.cargos);
    const { tramites } = response.data;
    delete response.data.tramites;
    if (Array.isArray(tramites)) {
      response.data.tramites_bloqueando_adeudos = tramites.map((t) => t.id);
    } else if (tramites) {
      response.data.tramites_bloqueando_adeudos = [tramites.id];
    } else {
      response.data.tramites_bloqueando_adeudos = [];
    }
    return formatReceived(response.data);
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Placa
 * @returns {Promise<Object>}
 * @param {number} id
*/
export const getPlaca = async (id) => {
  try {
    const response = await API.get(`/recaudacion/placas/${id}`);
    return formatReceived(response.data);
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Many Placas
 * @returns {Promise<Object>}
 * @param {Array} ids
 */
export const getManyPlacas = async (ids = []) => {
  try {
    if (!ids.length) return [];
    const promises = ids.map((id) => API.get(`/recaudacion/placas/${id}/`));
    const responses = await Promise.all(promises);
    return responses.map((e) => formatReceived(e.data));
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * Reasignar Placas
 * @param {Object} values
 * @returns {Promise<Boolean>}
 * */
export const postReasignarPlacas = async (values) => {
  try {
    const response = await API.post('/recaudacion/cambiar-asignaciones-de-placas/', values);
    if (values.usuario_final) {
      onSuccess(response, 'Placas reasignadas con éxito.');
    } else {
      onSuccess(response, 'Placas dadas de baja con éxito.');
    }
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * POST para obtener poliza
 * @param {object} values
 * @returns {promise<Object>}
 */
export const postPolizaDeIngresos = async (values) => {
  try {
    const response = await API.post('/recaudacion/enviar-poliza-de-ingresos/', values);
    onSuccess(response, 'Poliza de ingresos generada con éxito.');
    const base64 = `data:application/pdf;base64,${response.data.data}`;
    return { base64, success: true };
  } catch (err) {
    onError(err);
    return { success: false };
  }
};

/**
 * GET Tipos de motivos de multas
 * @param {object} params
 * @returns {promise<Object>}
 */
export const getTiposDeMotivos = async (params) => {
  try {
    const response = await API.get('recaudacion/motivos-multas/', { params });
    return response?.data || [];
  } catch (error) {
    onError(error);
    return [];
  }
};

// export const getClasificadores = async (_params = null) => {
//   try {
//     const { entidad } = store.getState().auth;
//     const params = { ..._params, entidad: entidad.id };
//     const response = await API.get(, { params });
//     return response.data;
//   } catch (error) {
//     onError(error);
//     return [];
//   }
// };

/**
 * GET Razones de Rechazo de Citas
 * @param {Object} params
 * @returns {Promise<object[]>}
 */
export const getRazonesDeRechazoCita = async (params = null) => {
  try {
    const response = await API.get('/recaudacion/razones-de-rechazos-para-citas/', { params });
    return response.data;
  } catch (error) {
    onError(error);
    return [];
  }
};

/**
 * POST - recalcula los cargos al valor anterior
 * de la moneda asignada en caso de que haya cambiado
 * @param {string} folio
 * @returns Promise<Boolean>
*/
export const postCargosReferenciados = async (folio) => {
  try {
    const values = { folio };
    const { entidad } = store.getState().auth;
    values.entidad = entidad.id;
    const response = await API.post('/recaudacion/referencias-de-pago/cargos-referenciados/', values);
    onSuccess(response);
    return {
      ...response.data,
      cargos: formatReceived(parseCargos(response.data.cargos)),
    };
  } catch (error) {
    onError(error);
    return { cargos: [] };
  }
};

/**
 * GET Declaracion en PDF
 * @param {number} id
 * @returns {Promise<string>} base64
*/
export const getDeclaracionPDF = async (id) => {
  try {
    const response = await API.get(`/empresas/declaraciones-empresariales-public/${id}/pdf/`);
    return `data:application/pdf;base64,${response.data.pdf}`;
  } catch (error) {
    onError(error);
  }
  return null;
};

/**
 * GET Clase de Servicios de Infracciones
 * @param {Object} params
 * @returns {Promise<object[]>}
 */
export const getClasesDeServicios = async (params = null) => {
  try {
    const response = await API.get('/recaudacion/clases-de-servicios-de-infraciones/', { params });
    return response.data;
  } catch (error) {
    onError(error);
    return [];
  }
};
/**
 * GET Rutas de Servicios Publicos
 * @param {Object} params
 * @returns {Promise<object[]>}
 */
export const getRutasDeServiciosPublicos = async (params = null) => {
  try {
    const response = await API.get('/recaudacion/rutas-servicios-publicos/', { params });
    return response.data;
  } catch (error) {
    onError(error);
    return [];
  }
};

/**
 * GET Notificaciones de Infracciones
 * @param {Object} params
 * @returns {Promise<object[]>}
 */
export const getNotificacionesDeInfracciones = async (params = null) => {
  try {
    const response = await API.get('/recaudacion/notificaciones-de-infracciones/', { params });
    return response.data;
  } catch (error) {
    onError(error);
    return [];
  }
};

/**
 * GET Sentidos
 * @returns {Promise<object[]>}
*/
export const getSentidos = async () => {
  try {
    const response = await API.get('/recaudacion/sentidos');
    return response.data;
  } catch (error) {
    onError(error);
  }
  return [];
};

export const getTiposDePanteones = async (params) => {
  try {
    const response = await API.get('recaudacion/tipos-de-panteones/', { params });
    return response.data;
  } catch (error) {
    onError(error);
    return [];
  }
};

export const getTipoDePanteon = async (id) => {
  try {
    const response = await API.get(`recaudacion/tipos-de-panteones/${id}/`);
    return response.data;
  } catch (error) {
    onError(error);
    return [];
  }
};

export const getTiposDeContratos = async (params) => {
  try {
    const response = await API.get('recaudacion/tipos-de-contratos-panteones/', { params });
    return response.data;
  } catch (error) {
    onError(error);
    return [];
  }
};
export const getTipoDeContrato = async (id) => {
  try {
    const response = await API.get(`recaudacion/tipos-de-contratos-panteones/${id}/`);
    return response.data;
  } catch (error) {
    onError(error);
    return [];
  }
};

export const getTiposDeEspacios = async (params) => {
  try {
    const response = await API.get('recaudacion/tipos-de-espacios-panteones/', { params });
    return response.data;
  } catch (error) {
    onError(error);
    return [];
  }
};

export const getTipoDeEspacio = async (id) => {
  try {
    const response = await API.get(`recaudacion/tipos-de-espacios-panteones/${id}/`);
    return response.data;
  } catch (error) {
    onError(error);
    return [];
  }
};

export const getMedidasDeLotesDePanteon = async (params) => {
  try {
    const response = await API.get('recaudacion/medidas-lotes-de-panteones/', { params });
    return response.data;
  } catch (error) {
    onError(error);
    return [];
  }
};

export const getTiposDeMedidasDeLotesDePanteon = async (params) => {
  try {
    const response = await API.get('recaudacion/tipos-de-medidas-de-lotes-panteones/', { params });
    return response.data;
  } catch (error) {
    onError(error);
    return [];
  }
};

export const getUnidadesDeMedida = async (params) => {
  try {
    const response = await API.get('recaudacion/unidades-de-medidas-lotes/', { params });
    return response.data;
  } catch (error) {
    onError(error);
    return [];
  }
};

export const postRecalcularRecargos = async (values) => {
  try {
    const response = await API.post('recaudacion/cargos/recalcular-multiple/', values);
    return response.data;
  } catch (error) {
    onError(error);
    return [];
  }
};

/**
 * POST Verifica los importes de un convenio
 * @param {Object} values - Valores para verificar los importes del convenio
 * @returns {Promise<Object>} Retorna un objeto con la siguiente estructura:
 * - Si el status es 200:
 *   {
 *     status: "Recalculado correcto",
 *     se_recalculo_importe: true
 *   }
 * - Si el status es 400:
 *   {
 *     necesita_recalculo: true
 *   }
 */
export const postVerificarImportesConvenio = async (values) => {
  try {
    const response = await API.post('recaudacion/verificar-importes-convenio/', values, { accionInf: 'CONVENIO' });
    return response.data;
  } catch (error) {
    if (error.response?.status === 400) {
      return error.response.data;
    }
    onError(error);
    return { necesita_recalculo: false };
  }
};
